/**
 * ChatPage — Backoffice Avanzado para Agentes (Fase 3b + 3c)
 *
 * 3 vistas:
 *  1. Conversaciones — panel clásico en tiempo real con historial, búsqueda y respuestas rápidas
 *  2. Cola — waiting queue con selección múltiple y bulk-assign
 *  3. Equipo — métricas por agente, gráfico y alertas de inactividad
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useOrganization, useUser } from '@clerk/clerk-react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../lib/api';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell,
} from 'recharts';
import {
  MessageSquare, User, Clock, RefreshCw, Send, UserCheck,
  ArrowRightLeft, X, Bot, Wifi, WifiOff, Search, Bell, BellOff,
  Zap, Plus, Trash2, Edit2, Save, History, AlertCircle,
  CheckSquare, Square, Users, BarChart2, Star, StarOff,
  ChevronRight, CheckCircle, MessageCircle, Globe, ExternalLink, Languages, Paperclip,
  Eye, EyeOff,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { usePresence, getOtherViewers } from '../hooks/usePresence';
import { ViewersStack, ViewersBanner } from '../components/ViewersIndicator';
import { CustomerThreadsBanner } from '../components/CustomerThreadsBanner';

// ── Constants & helpers ───────────────────────────────────────

// Mejor nombre disponible para un visitante
function getVisitorName(conv) {
  if (!conv) return 'Visitante';
  if (conv.visitor_name) return conv.visitor_name;
  if (conv.customer_email) return conv.customer_email;
  if (conv.visitor_email) return conv.visitor_email;
  return 'Visitante';
}

const STATUS_LABELS = {
  bot:     { label: 'Bot',          color: 'bg-blue-500/20 text-blue-300 border-blue-500/30' },
  waiting: { label: 'Esperando',    color: 'bg-amber-500/20 text-amber-300 border-amber-500/30' },
  agent:   { label: 'Respondidas',  color: 'bg-green-500/20 text-green-300 border-green-500/30' },
  closed:  { label: 'Cerrada',      color: 'bg-gray-500/20 text-gray-400 border-gray-500/30' },
};

function StatusBadge({ status }) {
  const s = STATUS_LABELS[status] || STATUS_LABELS.bot;
  return (
    <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded border ${s.color}`}>
      {s.label}
    </span>
  );
}

function ChannelBadge({ channel }) {
  if (channel !== 'whatsapp') return null;
  return (
    <span className="flex items-center gap-0.5 text-[10px] font-medium px-1.5 py-0.5 rounded border bg-green-500/15 text-green-400 border-green-500/30">
      <MessageCircle size={9} /> WA
    </span>
  );
}

function fmtSeconds(secs) {
  if (!secs) return '—';
  if (secs < 60) return `${secs}s`;
  if (secs < 3600) return `${Math.round(secs / 60)}m`;
  return `${Math.floor(secs / 3600)}h ${Math.round((secs % 3600) / 60)}m`;
}

function waitingLabel(ts) {
  if (!ts) return '';
  const mins = Math.floor((Date.now() - new Date(ts).getTime()) / 60000);
  if (mins < 1) return 'ahora';
  if (mins < 60) return `${mins}m`;
  return `${Math.floor(mins / 60)}h ${mins % 60}m`;
}

async function playBeep() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    // Resume is required if the AudioContext is suspended (common in idle tabs)
    if (ctx.state === 'suspended') await ctx.resume();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.frequency.value = 880;
    gain.gain.setValueAtTime(0.25, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.4);
    osc.start();
    osc.stop(ctx.currentTime + 0.4);
  } catch (_) {}
}

// ── MessageBubble helpers ─────────────────────────────────────

/** Splits text into plain segments and URL segments, rendering URLs as short clickable links */
function renderMsgContent(text) {
  if (!text) return null;
  const parts = text.split(/(https?:\/\/\S+)/g);
  if (parts.length === 1) return text;
  return parts.map((part, i) => {
    if (/^https?:\/\//.test(part)) {
      const display = part.length > 60 ? part.slice(0, 57) + '…' : part;
      return (
        <a key={i} href={part} target="_blank" rel="noopener noreferrer"
           title={part}
           className="underline underline-offset-2 opacity-70 hover:opacity-100 break-all transition-opacity">
          {display}
        </a>
      );
    }
    return part || null;
  });
}

// ── MessageBubble ─────────────────────────────────────────────

function MessageBubble({ msg }) {
  const isSystem  = msg.role === 'system';
  const isVisitor = msg.role === 'visitor';
  const isBot     = msg.role === 'bot';

  if (isSystem) {
    return (
      <div className="flex justify-center my-2">
        <span className="text-xs text-gray-500 bg-gray-800 px-3 py-1 rounded-full">{msg.content}</span>
      </div>
    );
  }
  return (
    <div className={`flex gap-2 min-w-0 ${isVisitor ? 'flex-row' : 'flex-row-reverse'} mb-3`}>
      <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-xs
        ${isVisitor ? 'bg-gray-700' : isBot ? 'bg-indigo-600' : 'bg-green-700'}`}>
        {isVisitor ? <User size={13} /> : isBot ? <Bot size={13} /> : <UserCheck size={13} />}
      </div>
      <div className="max-w-[75%] min-w-0">
        <div className={`text-[10px] mb-0.5 ${isVisitor ? 'text-gray-500' : 'text-gray-400 text-right'}`}>
          {isBot ? `Bot` : isVisitor ? 'Visitante' : 'Agente'}
          {' · '}{new Date(msg.timestamp).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
        </div>
        <div className={`px-3 py-2 rounded-2xl text-sm leading-relaxed break-words
          ${isVisitor ? 'bg-gray-800 text-gray-100 rounded-tl-sm'
            : isBot    ? 'bg-indigo-900/60 text-indigo-100 rounded-tr-sm border border-indigo-800/50'
                       : 'bg-green-900/50 text-green-100 rounded-tr-sm border border-green-800/50'}`}>
          {msg.image_url && (() => {
            const backendBase = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
            const absUrl = msg.image_url.startsWith('http')
              ? msg.image_url
              : `${backendBase}${msg.image_url.startsWith('/') ? '' : '/'}${msg.image_url}`;
            return (
              <a href={absUrl} target="_blank" rel="noopener noreferrer" className="block mb-1">
                <img src={absUrl} alt="adjunto"
                     data-testid="chat-msg-image"
                     onError={e => { e.target.style.display = 'none'; e.target.nextSibling && (e.target.nextSibling.style.display='flex'); }}
                     className="max-w-[220px] max-h-[220px] rounded-lg border border-white/10 hover:opacity-90 transition-opacity cursor-zoom-in" />
                <span style={{display:'none'}} className="text-xs text-gray-500 flex items-center gap-1">
                  <Paperclip size={11}/> imagen no disponible
                </span>
              </a>
            );
          })()}
          {renderMsgContent(msg.content)}
          {/* Texto original en español si fue auto-traducido */}
          {msg.auto_translated && msg.original_content && (
            <div className="mt-1.5 pt-1.5 border-t border-white/10 text-[11px] text-green-300/60 italic">
              ES: {msg.original_content}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Stats bar ─────────────────────────────────────────────────

function StatsBar({ stats }) {
  if (!stats) return null;
  const botCount = Math.max(0, (stats.total_conversations || 0) - (stats.waiting || 0) - (stats.active_with_agent || 0) - (stats.closed || 0));
  const items = [
    { label: 'Esperando',  value: stats.waiting,           color: 'text-amber-400',  bg: 'bg-amber-500/10 border-amber-500/20' },
    { label: 'Respondidas', value: stats.active_with_agent, color: 'text-green-400',  bg: 'bg-green-500/10 border-green-500/20' },
    { label: 'Bot',        value: botCount,                color: 'text-blue-400',   bg: 'bg-blue-500/10 border-blue-500/20' },
    { label: 'Cerradas',   value: stats.closed,            color: 'text-gray-400',   bg: 'bg-gray-700/30 border-gray-700' },
    { label: 'Workers WS', value: stats.workers_connected_ws, color: 'text-indigo-400', bg: 'bg-indigo-500/10 border-indigo-500/20' },
  ];
  return (
    <div className="flex gap-2 px-6 py-2 border-b border-gray-800 bg-gray-900/40 flex-wrap">
      {items.map(({ label, value, color, bg }) => (
        <div key={label} className={`flex items-center gap-2 px-3 py-1 rounded-lg border text-xs ${bg}`}>
          <span className="text-gray-500">{label}</span>
          <span className={`font-bold ${color}`}>{value ?? '—'}</span>
        </div>
      ))}
    </div>
  );
}

// ── Quick Replies Manager ─────────────────────────────────────

function QuickRepliesManager({ onClose }) {
  const [items, setItems] = useState([]);
  const [title, setTitle] = useState('');
  const [content, setCont] = useState('');
  const [editId, setEditId] = useState(null);

  const load = useCallback(async () => {
    try { const r = await api.get('/api/chat/quick-replies'); setItems(r.data.items || []); } catch {}
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!title.trim() || !content.trim()) return;
    try {
      editId
        ? await api.put(`/api/chat/quick-replies/${editId}`, { title, content })
        : await api.post('/api/chat/quick-replies', { title, content });
      setTitle(''); setCont(''); setEditId(null);
      await load();
    } catch { toast.error('Error al guardar'); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
      <div className="bg-gray-900 border border-gray-700 rounded-xl p-6 w-[480px] max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-white font-semibold flex items-center gap-2">
            <Zap size={15} className="text-amber-400" /> Respuestas rápidas
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white"><X size={16} /></button>
        </div>
        <div className="space-y-2 mb-4">
          <input value={title} onChange={e => setTitle(e.target.value)}
            placeholder="Título (ej: Saludo inicial)"
            className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-1.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500" />
          <textarea value={content} onChange={e => setCont(e.target.value)}
            placeholder="Texto de la respuesta..." rows={3}
            className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-1.5 text-sm text-white placeholder-gray-500 resize-none focus:outline-none focus:border-indigo-500" />
          <div className="flex gap-2">
            <Button size="sm" onClick={save} className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs gap-1">
              <Save size={12} /> {editId ? 'Actualizar' : 'Guardar'}
            </Button>
            {editId && (
              <Button size="sm" variant="outline" onClick={() => { setEditId(null); setTitle(''); setCont(''); }}
                className="border-gray-700 text-gray-400 text-xs">Cancelar</Button>
            )}
          </div>
        </div>
        <div className="flex-1 overflow-y-auto space-y-1.5">
          {items.length === 0
            ? <p className="text-gray-600 text-xs text-center py-4">Sin respuestas rápidas todavía</p>
            : items.map(item => (
              <div key={item.reply_id} className="flex items-start gap-2 p-2.5 bg-gray-800 rounded-lg border border-gray-700/50 group">
                <div className="flex-1 min-w-0">
                  <p className="text-white text-xs font-medium truncate">{item.title}</p>
                  <p className="text-gray-400 text-xs mt-0.5 line-clamp-2">{item.content}</p>
                </div>
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button onClick={() => { setEditId(item.reply_id); setTitle(item.title); setCont(item.content); }}
                    className="p-1 text-gray-400 hover:text-white"><Edit2 size={12} /></button>
                  <button onClick={async () => { await api.delete(`/api/chat/quick-replies/${item.reply_id}`); load(); }}
                    className="p-1 text-gray-400 hover:text-red-400"><Trash2 size={12} /></button>
                </div>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}

// ── Quick Replies Popover ─────────────────────────────────────

function QuickRepliesPopover({ onSelect, onClose }) {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    api.get('/api/chat/quick-replies').then(r => setItems(r.data.items || [])).catch(() => {});
  }, []);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [onClose]);

  const filtered = q
    ? items.filter(i => i.title.toLowerCase().includes(q.toLowerCase()) || i.content.toLowerCase().includes(q.toLowerCase()))
    : items;

  return (
    <div ref={ref} className="absolute bottom-full left-0 mb-2 w-72 bg-gray-900 border border-gray-700 rounded-xl shadow-2xl z-40 overflow-hidden">
      <div className="p-2 border-b border-gray-800">
        <input autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder="Buscar respuesta..."
          className="w-full bg-gray-800 text-sm text-white placeholder-gray-500 px-2.5 py-1.5 rounded-lg focus:outline-none" />
      </div>
      <div className="max-h-52 overflow-y-auto">
        {filtered.length === 0
          ? <p className="text-gray-600 text-xs text-center py-4">No hay respuestas</p>
          : filtered.map(item => (
            <button key={item.reply_id} onClick={() => onSelect(item.content)}
              className="w-full text-left px-3 py-2.5 hover:bg-gray-800 transition-colors border-b border-gray-800/50 last:border-0">
              <p className="text-white text-xs font-medium">{item.title}</p>
              <p className="text-gray-400 text-xs mt-0.5 line-clamp-1">{item.content}</p>
            </button>
          ))}
      </div>
    </div>
  );
}

// ── Visitor Panel (right sidebar) ─────────────────────────────

function prettyUrl(url) {
  if (!url) return '—';
  try {
    const u = new URL(url);
    const path = (u.pathname + u.search).replace(/\/$/, '') || '/';
    return decodeURIComponent(path);
  } catch { return url; }
}

function VisitorPanel({ conv }) {
  const [tab, setTab] = useState('info');
  const [history, setHistory] = useState([]);
  const [loadingH, setLoadH] = useState(false);

  useEffect(() => {
    if (tab !== 'history' || !conv?.visitor_id) return;
    setLoadH(true);
    api.get(`/api/chat/visitors/${conv.visitor_id}/history`)
       .then(r => setHistory(r.data.conversations || []))
       .catch(() => {})
       .finally(() => setLoadH(false));
  }, [tab, conv?.visitor_id]);

  if (!conv) return null;

  const pages = (conv.pages_visited || []).slice().reverse(); // más reciente primero
  const pageCount = (conv.pages_visited || []).length;

  return (
    <div className="w-64 border-l border-gray-800 flex flex-col flex-shrink-0">
      <div className="flex border-b border-gray-800">
        {[
          { id: 'info',    Icon: User,    label: 'Info' },
          { id: 'pages',   Icon: Globe,   label: 'Rutas' },
          { id: 'history', Icon: History, label: 'Historial' },
        ].map(({ id, Icon, label }) => (
          <button key={id} onClick={() => setTab(id)}
            data-testid={`visitor-${id}-tab`}
            className={`flex-1 py-2 text-xs font-medium flex items-center justify-center gap-1 transition-colors
              ${tab === id ? 'text-indigo-400 border-b-2 border-indigo-500' : 'text-gray-500 hover:text-gray-300'}`}>
            <Icon size={11} /> {label}
            {id === 'pages' && pageCount > 0 && (
              <span className="text-[9px] bg-gray-700 text-gray-300 rounded-full px-1.5 leading-tight">{pageCount}</span>
            )}
          </button>
        ))}
      </div>
      <div className="flex-1 overflow-y-auto p-3">
        {tab === 'info' && (
          <div className="space-y-2.5 text-xs">
            {[
              ['Nombre',  conv.visitor_name  || conv.customer_email || conv.visitor_email || '—'],
              ['Email',   conv.visitor_email || '—'],
              ['Idioma',  (conv.language || 'es').toUpperCase()],
              ['Iniciada', new Date(conv.created_at).toLocaleString('es-ES', { day:'2-digit',month:'2-digit',year:'2-digit',hour:'2-digit',minute:'2-digit' })],
              ['Estado',  null],
              ...(conv.bot_turns > 0   ? [['Turnos bot',   String(conv.bot_turns)]]   : []),
              ...(conv.visitor_domain  ? [['Dominio',       conv.visitor_domain]]      : []),
              ...(conv.rating          ? [['Valoración',    '★'.repeat(conv.rating) + '☆'.repeat(5 - conv.rating)]] : []),
            ].map(([label, val]) => (
              <div key={label}>
                <span className="text-gray-500">{label}</span>
                {val === null
                  ? <div className="mt-0.5"><StatusBadge status={conv.status} /></div>
                  : <p className="text-white mt-0.5 break-all">{val}</p>
                }
              </div>
            ))}
          </div>
        )}
        {tab === 'pages' && (
          <div className="space-y-3 text-xs" data-testid="visitor-pages-panel">
            <div>
              <span className="text-gray-500 flex items-center gap-1"><Globe size={11} /> Página actual</span>
              {conv.current_page ? (
                <a href={conv.current_page} target="_blank" rel="noopener noreferrer"
                   data-testid="visitor-current-page"
                   title={conv.current_page}
                   className="mt-1 flex items-start gap-1 text-emerald-400 hover:text-emerald-300 break-all leading-snug">
                  <ExternalLink size={11} className="mt-0.5 flex-shrink-0" />
                  <span>{prettyUrl(conv.current_page)}</span>
                </a>
              ) : <p className="text-gray-600 mt-1">Sin datos de navegación</p>}
            </div>
            <div>
              <span className="text-gray-500">Páginas visitadas</span>
              {pages.length === 0 ? (
                <p className="text-gray-600 mt-1">—</p>
              ) : (
                <div className="mt-1.5 space-y-1.5">
                  {pages.map((p, i) => (
                    <a key={i} href={p.url} target="_blank" rel="noopener noreferrer"
                       data-testid={`visitor-page-${i}`}
                       title={p.url}
                       className={`block p-2 rounded-md border leading-snug break-all transition-colors
                         ${i === 0 && conv.current_page === p.url
                           ? 'bg-emerald-900/20 border-emerald-700/40 text-emerald-300'
                           : 'bg-gray-800/60 border-gray-700/50 text-gray-300 hover:border-gray-600'}`}>
                      <span className="flex items-center gap-1 text-gray-500 text-[10px] mb-0.5">
                        <ExternalLink size={9} />
                        {p.at ? new Date(p.at).toLocaleTimeString('es-ES', { hour:'2-digit', minute:'2-digit' }) : ''}
                      </span>
                      {p.title ? <span className="text-white block">{p.title}</span> : null}
                      <span className="text-[11px]">{prettyUrl(p.url)}</span>
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
        {tab === 'history' && (
          <div className="space-y-2">
            {loadingH
              ? <div className="flex items-center justify-center py-6"><RefreshCw size={16} className="animate-spin text-gray-500" /></div>
              : history.length === 0
                ? <p className="text-gray-600 text-xs text-center py-4">Sin historial previo</p>
                : history.map(h => (
                  <div key={h.conversation_id}
                       data-testid={`history-conv-${h.conversation_id}`}
                       className={`p-2.5 rounded-lg border text-xs ${
                         h.conversation_id === conv.conversation_id
                           ? 'bg-indigo-900/30 border-indigo-700/50'
                           : 'bg-gray-800/60 border-gray-700/50'
                       }`}>
                    <div className="flex items-center justify-between mb-1">
                      <StatusBadge status={h.status} />
                      <span className="text-gray-500 text-[10px]">
                        {new Date(h.created_at).toLocaleDateString('es-ES', { day:'2-digit',month:'2-digit',year:'2-digit' })}
                      </span>
                    </div>
                    <p className="text-gray-400 line-clamp-2 leading-relaxed">{h.last_message?.content || '—'}</p>
                    <div className="flex items-center justify-between mt-1">
                      <span className="text-gray-600 text-[10px]">{h.message_count || 0} mensajes</span>
                      {h.rating && <span className="text-amber-400 text-[10px]">{'★'.repeat(h.rating)}</span>}
                    </div>
                  </div>
                ))
            }
          </div>
        )}
      </div>
    </div>
  );
}

// ── Vista Cola ────────────────────────────────────────────────

function QueueView({ workers }) {
  const [queue, setQueue]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [assignTo, setAssignTo] = useState('me');
  const [assigning, setAssigning] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/api/chat/conversations/queue');
      setQueue(r.data.conversations || []);
    } catch {}
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, [load]);

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    setSelected(prev => prev.size === queue.length ? new Set() : new Set(queue.map(c => c.conversation_id)));
  };

  const bulkAssign = async () => {
    if (selected.size === 0) return;
    setAssigning(true);
    try {
      const r = await api.post('/api/chat/conversations/bulk-assign', {
        conversation_ids: Array.from(selected),
        worker_id: assignTo,
      });
      toast.success(`${r.data.assigned} conversación(es) asignadas`);
      setSelected(new Set());
      await load();
    } catch { toast.error('Error al asignar'); }
    finally { setAssigning(false); }
  };

  const takeOne = async (convId) => {
    try {
      await api.post(`/api/chat/conversations/${convId}/assign`, { worker_id: 'me' });
      toast.success('Conversación tomada');
      await load();
    } catch { toast.error('Error'); }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Toolbar */}
      <div className="flex items-center gap-3 px-6 py-3 border-b border-gray-800 bg-gray-900/30">
        <button onClick={toggleAll}
          data-testid="queue-select-all"
          className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-white transition-colors">
          {selected.size === queue.length && queue.length > 0
            ? <CheckSquare size={14} className="text-indigo-400" />
            : <Square size={14} />}
          {selected.size > 0 ? `${selected.size} seleccionadas` : 'Seleccionar todo'}
        </button>
        {selected.size > 0 && (
          <>
            <select
              value={assignTo} onChange={e => setAssignTo(e.target.value)}
              className="bg-gray-800 border border-gray-700 rounded px-2 py-1 text-xs text-white focus:outline-none focus:border-indigo-500">
              <option value="me">Yo mismo</option>
              {workers.filter(w => w.is_online).map(w => (
                <option key={w.user_id} value={w.user_id}>
                  {w.name || w.email || w.user_id}
                </option>
              ))}
            </select>
            <Button size="sm" onClick={bulkAssign} disabled={assigning}
              data-testid="bulk-assign-btn"
              className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs gap-1">
              {assigning ? <RefreshCw size={11} className="animate-spin" /> : <UserCheck size={11} />}
              Asignar ({selected.size})
            </Button>
          </>
        )}
        <div className="ml-auto flex items-center gap-2">
          <span className="text-xs text-gray-500">{queue.length} en cola</span>
          <button onClick={load} className="text-gray-500 hover:text-white transition-colors">
            <RefreshCw size={13} />
          </button>
        </div>
      </div>

      {/* Queue list */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center h-40">
            <RefreshCw size={20} className="animate-spin text-gray-500" />
          </div>
        ) : queue.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-40 text-gray-600">
            <CheckCircle size={28} className="mb-2 opacity-30" />
            <p className="text-sm">Cola vacía — sin conversaciones esperando</p>
          </div>
        ) : queue.map(conv => {
          const waitMins = Math.floor((conv.wait_seconds || 0) / 60);
          const isUrgent = waitMins >= 10;
          return (
            <div key={conv.conversation_id}
              className={`flex items-start gap-3 px-5 py-3.5 border-b border-gray-800/60 hover:bg-gray-900/40 transition-colors
                ${selected.has(conv.conversation_id) ? 'bg-indigo-900/10 border-l-2 border-l-indigo-500' : ''}`}>
              {/* Checkbox */}
              <button onClick={() => toggleSelect(conv.conversation_id)}
                data-testid={`queue-select-${conv.conversation_id}`}
                className="mt-0.5 text-gray-500 hover:text-indigo-400 transition-colors flex-shrink-0">
                {selected.has(conv.conversation_id)
                  ? <CheckSquare size={15} className="text-indigo-400" />
                  : <Square size={15} />}
              </button>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-white text-sm font-medium truncate">
                    {getVisitorName(conv)}
                  </span>
                  <span className={`text-xs flex items-center gap-1 flex-shrink-0 ${isUrgent ? 'text-red-400' : 'text-amber-400'}`}>
                    <Clock size={10} />
                    {conv.wait_seconds < 60 ? `${conv.wait_seconds}s` : `${Math.floor(conv.wait_seconds / 60)}m`}
                  </span>
                </div>
                {conv.visitor_email && (
                  <p className="text-gray-500 text-xs truncate">{conv.visitor_email}</p>
                )}
                <p className="text-gray-400 text-xs truncate mt-0.5">
                  {conv.last_message?.content || '—'}
                </p>
              </div>

              {/* Take button */}
              <Button size="sm" onClick={() => takeOne(conv.conversation_id)}
                data-testid={`take-conv-${conv.conversation_id}`}
                className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs gap-1 flex-shrink-0 self-center">
                <UserCheck size={11} /> Tomar
              </Button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Vista Equipo ──────────────────────────────────────────────

const PERIOD_OPTIONS = [
  { value: 'today', label: 'Hoy' },
  { value: 'week',  label: 'Semana' },
  { value: 'month', label: 'Mes' },
];

function StarRating({ value }) {
  if (!value) return <span className="text-gray-600 text-xs">—</span>;
  return (
    <span className="text-amber-400 text-sm">
      {'★'.repeat(Math.round(value))}{'☆'.repeat(5 - Math.round(value))}
      <span className="text-gray-400 text-xs ml-1">({value.toFixed(1)})</span>
    </span>
  );
}

function TeamView() {
  const [period, setPeriod] = useState('today');
  const [team, setTeam]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [alertThreshold, setAlertThreshold] = useState(
    () => parseInt(localStorage.getItem('sera_alert_threshold') || '30', 10)
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/api/chat/workers/team-stats?period=${period}`);
      setTeam(r.data.team || []);
    } catch {}
    finally { setLoading(false); }
  }, [period]);

  useEffect(() => { load(); }, [load]);

  const saveThreshold = (v) => {
    const n = parseInt(v, 10);
    if (!isNaN(n) && n > 0) {
      setAlertThreshold(n);
      localStorage.setItem('sera_alert_threshold', String(n));
    }
  };

  // Bar chart data
  const chartData = team.map(w => ({
    name: (w.name || w.worker_id).split(' ')[0].substring(0, 10),
    total: w.metrics.total || 0,
    closed: w.metrics.closed || 0,
  }));

  return (
    <div className="flex-1 overflow-y-auto p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-white font-semibold flex items-center gap-2">
            <Users size={16} className="text-indigo-400" /> Rendimiento del equipo
          </h2>
          <p className="text-gray-500 text-xs mt-0.5">Métricas agregadas por período</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Alert threshold */}
          <div className="flex items-center gap-1.5 text-xs text-gray-400">
            <AlertCircle size={12} className="text-amber-400" />
            Alertar si sin respuesta
            <input type="number" min="1" max="120"
              value={alertThreshold}
              onChange={e => saveThreshold(e.target.value)}
              className="w-12 bg-gray-800 border border-gray-700 rounded px-1.5 py-0.5 text-white text-xs text-center focus:outline-none focus:border-indigo-500"
            />
            min
          </div>
          {/* Period tabs */}
          <div className="flex bg-gray-800 rounded-lg p-0.5 gap-0.5">
            {PERIOD_OPTIONS.map(({ value, label }) => (
              <button key={value} onClick={() => setPeriod(value)}
                data-testid={`period-tab-${value}`}
                className={`px-3 py-1 rounded-md text-xs font-medium transition-colors
                  ${period === value ? 'bg-indigo-600 text-white' : 'text-gray-400 hover:text-white'}`}>
                {label}
              </button>
            ))}
          </div>
          <button onClick={load} className="text-gray-500 hover:text-white transition-colors">
            <RefreshCw size={13} />
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-40">
          <RefreshCw size={20} className="animate-spin text-gray-500" />
        </div>
      ) : team.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-40 text-gray-600">
          <Users size={28} className="mb-2 opacity-30" />
          <p className="text-sm">Sin datos de workers todavía</p>
          <p className="text-xs mt-1 text-gray-700">Los workers deben conectarse al menos una vez</p>
        </div>
      ) : (
        <>
          {/* Bar chart */}
          {chartData.some(d => d.total > 0) && (
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-4 mb-6">
              <p className="text-gray-400 text-xs font-medium mb-3">Conversaciones por agente</p>
              <ResponsiveContainer width="100%" height={160}>
                <BarChart data={chartData} barGap={4} barSize={28}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" vertical={false} />
                  <XAxis dataKey="name" tick={{ fill: '#6b7280', fontSize: 11 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: '#6b7280', fontSize: 11 }} axisLine={false} tickLine={false} />
                  <Tooltip
                    contentStyle={{ background: '#111827', border: '1px solid #374151', borderRadius: 8, fontSize: 12 }}
                    labelStyle={{ color: '#e5e7eb' }}
                    cursor={{ fill: 'rgba(99,102,241,0.08)' }}
                  />
                  <Bar dataKey="total" name="Total" fill="#6366f1" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="closed" name="Cerradas" fill="#10b981" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Agent cards grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {team.map(w => {
              const m = w.metrics;
              const hasAlert = m.stale_conversations > 0;
              return (
                <div key={w.worker_id}
                  data-testid={`agent-card-${w.worker_id}`}
                  className={`bg-gray-900 border rounded-xl p-4 ${
                    hasAlert ? 'border-amber-500/40' : 'border-gray-800'
                  }`}>
                  {/* Header */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold
                        ${w.is_online ? 'bg-green-700 text-green-100' : 'bg-gray-700 text-gray-400'}`}>
                        {(w.name || 'A').charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <p className="text-white text-sm font-medium leading-tight truncate max-w-[120px]">
                          {w.name || w.email || w.worker_id}
                        </p>
                        <p className={`text-[10px] ${w.is_online ? 'text-green-400' : w.registered === false ? 'text-yellow-600' : 'text-gray-500'}`}>
                          {w.is_online ? 'Online' : w.registered === false ? 'Sin activar' : 'Offline'}
                        </p>
                      </div>
                    </div>
                    {hasAlert && (
                      <div className="flex items-center gap-1 text-amber-400 text-[10px]"
                           title={`${m.stale_conversations} conversación(es) sin respuesta hace más de ${alertThreshold}min`}>
                        <AlertCircle size={13} />
                        <span>{m.stale_conversations}</span>
                      </div>
                    )}
                  </div>

                  {/* Metrics grid */}
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="bg-gray-800/60 rounded-lg p-2.5">
                      <p className="text-gray-500 text-[10px]">Conversaciones</p>
                      <p className="text-white font-bold text-lg leading-tight">{m.total}</p>
                      <p className="text-gray-500 text-[10px]">{m.closed} cerradas</p>
                    </div>
                    <div className="bg-gray-800/60 rounded-lg p-2.5">
                      <p className="text-gray-500 text-[10px]">1ª respuesta</p>
                      <p className="text-white font-bold text-base leading-tight">{fmtSeconds(m.avg_first_response_seconds)}</p>
                      <p className="text-gray-500 text-[10px]">promedio</p>
                    </div>
                    <div className="bg-gray-800/60 rounded-lg p-2.5">
                      <p className="text-gray-500 text-[10px]">Resolución</p>
                      <p className="text-white font-bold text-base leading-tight">{fmtSeconds(m.avg_resolution_seconds)}</p>
                      <p className="text-gray-500 text-[10px]">promedio</p>
                    </div>
                    <div className="bg-gray-800/60 rounded-lg p-2.5">
                      <p className="text-gray-500 text-[10px]">Valoración</p>
                      <StarRating value={m.avg_rating} />
                      {m.ratings_count > 0 && (
                        <p className="text-gray-500 text-[10px]">{m.ratings_count} votos</p>
                      )}
                    </div>
                  </div>

                  {/* Active/Waiting indicators */}
                  {(m.active > 0 || m.waiting > 0) && (
                    <div className="flex gap-2 mt-2.5 text-[10px]">
                      {m.active > 0 && (
                        <span className="bg-green-500/10 border border-green-500/20 text-green-400 px-2 py-0.5 rounded">
                          {m.active} activas
                        </span>
                      )}
                      {m.waiting > 0 && (
                        <span className="bg-amber-500/10 border border-amber-500/20 text-amber-400 px-2 py-0.5 rounded">
                          {m.waiting} esperando
                        </span>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

// ── Reply Input (memoized — isolates typing state from parent re-renders) ────

/**
 * Isolated input component. By using React.memo with a stable `onSend` prop,
 * this component is immune to the parent's re-renders caused by WS messages,
 * conversation list updates, visitor typing indicators, etc.
 * Using key={conv.conversation_id} on the parent side resets the text state
 * automatically whenever the agent switches to a different conversation,
 * eliminating the "message sent to wrong chat" bug.
 */
const ReplyInput = React.memo(function ReplyInput({ convStatus, onSend, convId, orgId }) {
  const [text,         setText]         = useState('');
  const [sending,      setSending]      = useState(false);
  const [qrPopover,    setQrPopover]    = useState(false);
  const [pendingImage, setPendingImage] = useState(null);   // { url, preview }
  const [imgUploading, setImgUploading] = useState(false);
  const fileInputRef                   = useRef(null);

  const handleImagePick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImgUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('conv_id', convId);
      const { data } = await api.post('/api/chat/upload-image-agent', fd, {
        params: { organization_id: orgId },
      });
      const preview = URL.createObjectURL(file);
      setPendingImage({ url: data.image_url, preview });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error subiendo imagen');
    } finally {
      setImgUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSend = async () => {
    if (sending || (!text.trim() && !pendingImage)) return;
    const textToSend  = text;
    const imageToSend = pendingImage?.url || null;
    setText('');
    setPendingImage(null);
    setSending(true);
    try {
      await onSend(textToSend, imageToSend);
    } catch {
      setText(textToSend);
      if (imageToSend) setPendingImage({ url: imageToSend, preview: null });
    } finally {
      setSending(false);
    }
  };

  if (convStatus === 'closed') {
    return <p className="text-gray-500 text-xs text-center py-2">Conversación cerrada</p>;
  }

  return (
    <div className="flex flex-col gap-1.5">
      {/* Image preview */}
      {pendingImage && (
        <div className="flex items-center gap-2 bg-gray-800 rounded-lg px-2 py-1.5">
          <img src={pendingImage.preview || ''} alt="adjunto"
               className="h-12 w-12 object-cover rounded" />
          <span className="text-xs text-gray-400 flex-1 truncate">Imagen adjunta</span>
          <button onClick={() => setPendingImage(null)}
                  className="text-gray-500 hover:text-red-400 transition-colors">
            <X size={13} />
          </button>
        </div>
      )}
      <div className="flex gap-2 items-end">
        {/* Quick replies */}
        <div className="relative self-end mb-0.5">
          <button data-testid="quick-replies-btn" onClick={() => setQrPopover(v => !v)}
            className="p-2 rounded-lg bg-gray-800 hover:bg-gray-700 text-amber-400 hover:text-amber-300 transition-colors"
            title="Respuestas rápidas">
            <Zap size={14} />
          </button>
          {qrPopover && (
            <QuickRepliesPopover
              onSelect={(t) => { setText(t); setQrPopover(false); }}
              onClose={() => setQrPopover(false)}
            />
          )}
        </div>
        {/* Image attach button */}
        <button
          data-testid="agent-attach-image-btn"
          onClick={() => fileInputRef.current?.click()}
          disabled={imgUploading || !!pendingImage}
          title="Adjuntar imagen"
          className="self-end mb-0.5 p-2 rounded-lg bg-gray-800 hover:bg-gray-700 text-gray-400 hover:text-indigo-300 transition-colors disabled:opacity-40">
          {imgUploading ? <RefreshCw size={14} className="animate-spin" /> : <Paperclip size={14} />}
        </button>
        <input ref={fileInputRef} type="file" accept="image/*" className="hidden"
               data-testid="agent-image-file-input" onChange={handleImagePick} />
        <textarea
          data-testid="reply-textarea"
          value={text}
          onChange={e => setText(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
          placeholder="Escribe tu respuesta... (Enter para enviar)"
          rows={2}
          className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 resize-none focus:outline-none focus:border-indigo-500 transition-colors"
        />
        <Button data-testid="send-reply-btn" onClick={handleSend}
          disabled={sending || (!text.trim() && !pendingImage)}
          className="bg-indigo-600 hover:bg-indigo-700 text-white self-end px-3 py-2">
          {sending ? <RefreshCw size={14} className="animate-spin" /> : <Send size={14} />}
        </Button>
      </div>
    </div>
  );
});

// ── Vista Conversaciones (principal) ──────────────────────────

function ConversationsView({ workers, loadStats, wsRef, wsConnected, soundEnabled, stats }) {
  const { organization } = useOrganization();
  const orgId = organization?.id;
  const [searchParams, setSearchParams] = useSearchParams();

  const [conversations, setConversations] = useState([]);
  const [convLoading,   setConvLoading]   = useState(true);
  const [selectedConv,  setSelectedConv]  = useState(null);
  const [messages,      setMessages]      = useState([]);
  const [filter,        setFilter]        = useState('waiting');
  const [searchQuery,   setSearchQuery]   = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [autoTranslate, setAutoTranslate] = useState(false);
  const [transferModal, setTransferModal] = useState(false);
  const [qrManager,     setQrManager]     = useState(false);
  const [visitorTyping, setVisitorTyping] = useState({});

  const messagesEndRef       = useRef(null);
  const messagesContainerRef = useRef(null);
  const isNearBottomRef      = useRef(true);
  const sendReplyFnRef       = useRef(null);
  const searchTimerRef       = useRef(null);
  const typingTimersRef      = useRef({});

  // ── Presencia (qué agentes están viendo qué conversación) ──
  const selectedConvKey = selectedConv ? `chat:${selectedConv.conversation_id}` : '';
  const { viewersByResource, viewersOthersOnCurrent, selfAgentId } = usePresence({
    orgId,
    resourceKey: selectedConvKey,
    externalWsRef: wsRef,
    externalWsConnected: wsConnected,
  });

  const loadConversations = useCallback(async () => {
    if (!orgId) return;
    setConvLoading(true);
    try {
      // Siempre enviamos el filtro activo al backend — evita la truncación client-side
      // en producción donde hay >100 conversaciones por estado.
      const url = `/api/chat/conversations?status=${filter}`;
      const res = await api.get(url);
      const newConvs = res.data.conversations || [];
      // Smart diff: only update React state if the data actually changed.
      // Prevents the list from flickering/re-rendering every polling cycle when nothing changed.
      setConversations(prev => {
        const sig = (arr) => arr.map(c => `${c.conversation_id}:${c.status}:${c.unread_count}`).join('|');
        return sig(prev) === sig(newConvs) ? prev : newConvs;
      });
    } catch {}
    finally { setConvLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, filter]);

  // Separate silent polling function (no loading spinner) used by the interval.
  const pollConversations = useCallback(async () => {
    if (!orgId) return;
    try {
      const url = `/api/chat/conversations?status=${filter}`;
      const res = await api.get(url);
      const newConvs = res.data.conversations || [];
      setConversations(prev => {
        const sig = (arr) => arr.map(c => `${c.conversation_id}:${c.status}:${c.unread_count}`).join('|');
        return sig(prev) === sig(newConvs) ? prev : newConvs;
      });
    } catch {}
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, filter]);

  const loadMessages = useCallback(async (conv) => {
    if (!conv) return;
    try {
      const res = await api.get(`/api/chat/conversations/${conv.conversation_id}`);
      setMessages(res.data.messages || []);
      // Refrescar datos de navegación/estado del panel con lo último del servidor
      if (res.data.conversation) {
        const fresh = res.data.conversation;
        setSelectedConv(prev => prev && prev.conversation_id === fresh.conversation_id
          ? { ...prev, current_page: fresh.current_page, pages_visited: fresh.pages_visited, unread_count: 0 }
          : prev);
      }
    } catch {}
  }, []);

  useEffect(() => {
    loadConversations();
    // Silent polling fallback: check for new conversations every 15s without showing a spinner.
    // Ensures new chats appear even if the WebSocket is interrupted (common behind Cloudflare).
    const t = setInterval(pollConversations, 15000);
    return () => clearInterval(t);
  }, [loadConversations, pollConversations]);
  useEffect(() => { if (selectedConv) loadMessages(selectedConv); }, [selectedConv, loadMessages]);

  // Deep-link: ?conv=<id> (ej. desde el banner "Este cliente tiene más conversaciones")
  useEffect(() => {
    const wanted = searchParams.get('conv');
    if (!wanted || selectedConv?.conversation_id === wanted) return;
    api.get(`/api/chat/conversations/${wanted}`).then(res => {
      if (res.data.conversation) setSelectedConv(res.data.conversation);
    }).catch(() => {}).finally(() => {
      setSearchParams(prev => { const p = new URLSearchParams(prev); p.delete('conv'); return p; }, { replace: true });
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Smart auto-scroll: only scroll to bottom when the user is already near the bottom.
  // This prevents forcibly jumping the agent back to the bottom while reading history.
  useEffect(() => {
    if (isNearBottomRef.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Whenever the agent switches to a different conversation, reset the "near bottom" flag
  // so the initial load of messages always scrolls to the bottom.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    isNearBottomRef.current = true;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedConv?.conversation_id]);

  // Search with debounce
  useEffect(() => {
    if (!searchQuery.trim()) { setSearchResults(null); return; }
    clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(async () => {
      try {
        const r = await api.get(`/api/chat/conversations/search?q=${encodeURIComponent(searchQuery)}`);
        setSearchResults(r.data.conversations || []);
      } catch { setSearchResults([]); }
    }, 350);
    return () => clearTimeout(searchTimerRef.current);
  }, [searchQuery]);

  // Stable ref — always holds latest callbacks/state without re-running the WS effect
  const cbRef = useRef({});
  cbRef.current = { loadConversations, pollConversations, loadStats, soundEnabled, conversations, selectedConv };

  // WS listener: set ONCE per connection (not per callback change) to avoid handler chain buildup
  useEffect(() => {
    const ws = wsRef?.current;
    if (!ws || !wsConnected) return;
    const origOnMessage = ws.onmessage;

    ws.onmessage = (e) => {
      // Always call parent handler (sound on escalated, etc.)
      if (typeof origOnMessage === 'function') origOnMessage(e);
      try {
        const msg = JSON.parse(e.data);
        const { loadConversations, pollConversations, loadStats, soundEnabled, conversations, selectedConv } = cbRef.current;

        if (msg.type === 'new_message') {
          // Only append to the open chat if the message belongs to the currently selected conversation.
          // Otherwise messages from other visitors would leak into the open thread (mixing conversations).
          if (selectedConv && msg.conversation_id === selectedConv.conversation_id) {
            setMessages(prev => {
              if (prev.some(m => m.message_id === msg.message?.message_id)) return prev;
              return [...prev, msg.message];
            });
          }
          setConversations(prev => prev.map(c => {
            if (c.conversation_id !== msg.conversation_id) return c;
            const updated = { ...c, last_message: msg.message, updated_at: msg.message.timestamp };
            // Aplicar cambio de estado si el backend lo incluye (ej: agente responde → status=agent)
            if (msg.new_status) updated.status = msg.new_status;
            const isSelected = selectedConv?.conversation_id === msg.conversation_id;
            if (msg.message?.role === 'visitor' && msg.pending && !isSelected) {
              // Mensaje pendiente de responder en una conversación no abierta → incrementa burbuja
              updated.unread_count = (c.unread_count || 0) + 1;
              updated.last_visitor_message_at = msg.message.timestamp;
            } else if (msg.message?.role === 'agent' || msg.message?.role === 'bot') {
              // Respondida → limpia la burbuja
              updated.unread_count = 0;
            }
            return updated;
          }));
          // Only beep on visitor messages when conversation is already in agent mode
          if (msg.message?.role === 'visitor') {
            // Un visitante que acaba de enviar un mensaje ya NO está escribiendo
            setVisitorTyping(prev => ({ ...prev, [msg.conversation_id]: false }));
            if (typingTimersRef.current[msg.conversation_id]) {
              clearTimeout(typingTimersRef.current[msg.conversation_id]);
              delete typingTimersRef.current[msg.conversation_id];
            }
            const conv = conversations.find(c => c.conversation_id === msg.conversation_id);
            if (conv?.status === 'agent' && soundEnabled) playBeep();
            if (document.hidden) {
              try { toast.info('Nuevo mensaje del visitante', { duration: 2500 }); } catch {}
            }
          }
        } else if (msg.type === 'visitor_page') {
          // El visitante navegó a otra URL → actualizar panel de rutas en vivo
          setConversations(prev => prev.map(c => {
            if (c.conversation_id !== msg.conversation_id) return c;
            const pv = (c.pages_visited || []).slice();
            if (msg.page && (!pv.length || pv[pv.length - 1].url !== msg.page.url)) pv.push(msg.page);
            return { ...c, current_page: msg.current_page, pages_visited: pv.slice(-40) };
          }));
          setSelectedConv(prev => {
            if (!prev || prev.conversation_id !== msg.conversation_id) return prev;
            const pv = (prev.pages_visited || []).slice();
            if (msg.page && (!pv.length || pv[pv.length - 1].url !== msg.page.url)) pv.push(msg.page);
            return { ...prev, current_page: msg.current_page, pages_visited: pv.slice(-40) };
          });
        } else if (msg.type === 'conversation_read') {
          // Otro agente (u otra pestaña) abrió la conversación → solo marcar revisada (no limpiar burbuja)
          setConversations(prev => prev.map(c =>
            c.conversation_id === msg.conversation_id
              ? { ...c, ...(msg.agent_reviewed ? { agent_reviewed: true } : {}) }
              : c
          ));
        } else if (['new_conversation', 'escalated', 'conversation_assigned', 'conversation_transferred', 'conversation_closed', 'conversation_reopened'].includes(msg.type)) {
          pollConversations();
          loadStats();
        } else if (msg.type === 'visitor_typing') {
          setVisitorTyping(prev => ({ ...prev, [msg.conversation_id]: msg.is_typing }));
          // Defensa anti-"escribiendo" pegado: si llega is_typing=true pero nunca un
          // is_typing=false (visitante cierra pestaña / se pierde el evento), auto-limpiar.
          const timers = typingTimersRef.current;
          if (timers[msg.conversation_id]) clearTimeout(timers[msg.conversation_id]);
          if (msg.is_typing) {
            timers[msg.conversation_id] = setTimeout(() => {
              setVisitorTyping(p => ({ ...p, [msg.conversation_id]: false }));
              delete typingTimersRef.current[msg.conversation_id];
            }, 6000);
          } else {
            delete timers[msg.conversation_id];
          }
        }
      } catch {}
    };

    // Cleanup: restore parent handler so it doesn't accumulate stale layers
    return () => {
      if (ws) ws.onmessage = origOnMessage;
    };
  }, [wsRef, wsConnected]); // Only depends on WS connection — callbacks come from cbRef

  // sendReplyFnRef holds the latest send logic — always reads current selectedConv/autoTranslate.
  // Called by the isolated ReplyInput child via stableSendReply.
  sendReplyFnRef.current = async (text, imageUrl = null) => {
    if (!selectedConv) throw new Error('no conv');
    const convLang = selectedConv.language || 'es';
    const shouldTranslate = autoTranslate && convLang !== 'es';
    const r = await api.post(`/api/chat/conversations/${selectedConv.conversation_id}/agent-message`, {
      content: text,
      auto_translate: shouldTranslate,
      image_url: imageUrl || undefined,
    });
    isNearBottomRef.current = true; // ensure the new message is visible after sending
    setMessages(prev => [...prev, r.data.message]);
    // Update selectedConv and the list immediately without waiting for WS
    setSelectedConv(prev => prev ? { ...prev, status: 'agent', unread_count: 0 } : prev);
    setConversations(prev => prev.map(c =>
      c.conversation_id === selectedConv.conversation_id
        ? { ...c, status: 'agent', unread_count: 0, last_message: r.data.message }
        : c
    ));
  };

  // Stable reference passed to ReplyInput — never changes, so ReplyInput never re-renders
  // due to this prop changing when parent state updates (WS messages, conv list, etc.)
  const stableSendReply = useCallback(async (text, imageUrl = null) => {
    try {
      await sendReplyFnRef.current(text, imageUrl);
    } catch { toast.error('Error al enviar'); }
  }, []);

  const handleMessagesScroll = useCallback(() => {
    const el = messagesContainerRef.current;
    if (!el) return;
    isNearBottomRef.current = (el.scrollHeight - el.scrollTop - el.clientHeight) < 80;
  }, []);

  const assignToMe = async () => {
    if (!selectedConv) return;
    try {
      await api.post(`/api/chat/conversations/${selectedConv.conversation_id}/assign`, { worker_id: 'me' });
      setSelectedConv(prev => prev ? { ...prev, status: 'agent', assigned_to: 'me' } : prev);
      setConversations(prev => prev.map(c =>
        c.conversation_id === selectedConv.conversation_id
          ? { ...c, status: 'agent', assigned_to: 'me' }
          : c
      ));
      await loadStats();
      toast.success('Conversación asignada');
    } catch { toast.error('Error al asignar'); }
  };

  const closeConv = async () => {
    if (!selectedConv) return;
    const convId = selectedConv.conversation_id;
    // Optimistic update: deselect and remove from list immediately (no visible delay)
    setSelectedConv(null);
    setConversations(prev => prev.filter(c => c.conversation_id !== convId));
    try {
      await api.post(`/api/chat/conversations/${convId}/close`, {});
      toast.success('Conversación cerrada');
      loadStats(); // refresh stats silently in background
    } catch {
      toast.error('Error al cerrar');
      loadConversations(); // restore list on failure
    }
  };

  const markAsUnread = async () => {
    if (!selectedConv) return;
    const convId = selectedConv.conversation_id;
    try {
      await api.post(`/api/chat/conversations/${convId}/mark-unread`);
      // Optimistic: add unread dot and deselect so the agent sees it highlighted in the list
      setConversations(prev => prev.map(c =>
        c.conversation_id === convId ? { ...c, unread_count: 1 } : c
      ));
      setSelectedConv(null);
      toast.success('Marcada como no leída');
    } catch { toast.error('Error al marcar'); }
  };

  const transferTo = async (workerId) => {
    if (!selectedConv) return;
    const convId = selectedConv.conversation_id;
    // Optimistic update: close modal and remove from list immediately
    setTransferModal(false);
    setSelectedConv(null);
    setConversations(prev => prev.filter(c => c.conversation_id !== convId));
    try {
      await api.post(`/api/chat/conversations/${convId}/transfer`, { to_worker_id: workerId, note: 'Traspaso manual' });
      toast.success('Traspasada');
      loadStats();
    } catch {
      toast.error('Error al traspasar');
      loadConversations();
    }
  };

  const isTyping = selectedConv ? visitorTyping[selectedConv.conversation_id] : false;

  // Contar conversaciones por estado para badges en tabs
  // waiting y bot usan stats (totales reales del DB), agent usa datos locales (mensajes sin leer)
  const _botCount = Math.max(0, (stats?.total_conversations || 0) - (stats?.waiting || 0) - (stats?.active_with_agent || 0) - (stats?.closed || 0));
  const tabCounts = {
    waiting: stats?.waiting || 0,
    agent:   conversations.filter(c => (c.unread_count || 0) > 0).length,
    bot:     _botCount,
  };

  const displayedConvs = searchResults !== null
    ? searchResults
    : [...conversations]
        .filter(c => c.status === filter)
        .sort((a, b) => {
        // Conversaciones pendientes de responder (con mensajes sin leer) van arriba del todo
        const ua = (a.unread_count || 0) > 0 ? 1 : 0;
        const ub = (b.unread_count || 0) > 0 ? 1 : 0;
        if (ua !== ub) return ub - ua;
        const ta = new Date(a.last_visitor_message_at || a.updated_at || a.created_at).getTime();
        const tb = new Date(b.last_visitor_message_at || b.updated_at || b.created_at).getTime();
        return tb - ta;
      });

  return (
    <div className="flex flex-1 overflow-hidden">
      {/* ── Sidebar ── */}
      <div className="w-80 border-r border-gray-800 flex flex-col flex-shrink-0">
        <div className="p-2 border-b border-gray-800">
          <div className="relative">
            <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-500" />
            <input
              data-testid="conversation-search-input"
              value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
              placeholder="Buscar por nombre, email..."
              className="w-full bg-gray-800 border border-gray-700 rounded-lg pl-7 pr-3 py-1.5 text-xs text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500"
            />
            {searchQuery && (
              <button onClick={() => { setSearchQuery(''); setSearchResults(null); }}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white">
                <X size={10} />
              </button>
            )}
          </div>
        </div>
        {!searchQuery && (
          <div className="flex border-b border-gray-800 overflow-x-auto">
            {['waiting','agent','bot','closed'].map(s => {
              const count = s === 'waiting' ? tabCounts.waiting
                          : s === 'agent'   ? tabCounts.agent
                          : s === 'bot'     ? tabCounts.bot : 0;
              return (
                <button key={s} onClick={() => setFilter(s)}
                  data-testid={`filter-tab-${s}`}
                  className={`flex-1 py-2 text-xs font-medium transition-colors flex items-center justify-center gap-1 whitespace-nowrap ${
                    filter === s ? 'text-indigo-400 border-b-2 border-indigo-500' : 'text-gray-500 hover:text-gray-300'}`}>
                  {STATUS_LABELS[s]?.label}
                  {count > 0 && (
                    <span className={`text-[9px] font-bold rounded-full min-w-[16px] h-[16px] px-1 flex items-center justify-center ${
                      s === 'waiting' ? 'bg-amber-500 text-white' : 'bg-indigo-500 text-white'
                    }`} data-testid={`tab-badge-${s}`}>
                      {count > 9 ? '9+' : count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}
        {searchQuery && searchResults !== null && (
          <div className="px-3 py-1.5 border-b border-gray-800">
            <span className="text-xs text-gray-500">{searchResults.length} resultado(s)</span>
          </div>
        )}
        <div className="flex-1 overflow-y-auto">
          {convLoading ? (
            <div className="flex flex-col items-center justify-center h-40 text-gray-600">
              <div className="w-5 h-5 border-2 border-indigo-500/30 border-t-indigo-500 rounded-full animate-spin mb-2" />
              <p className="text-xs">Cargando...</p>
            </div>
          ) : displayedConvs.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-40 text-gray-600">
              <MessageSquare size={24} className="mb-2 opacity-40" />
              <p className="text-xs">{searchQuery ? 'Sin resultados' : 'No hay conversaciones'}</p>
            </div>
          ) : displayedConvs.map(conv => {
            const unread = conv.unread_count || 0;
            const hasUnread = unread > 0 && selectedConv?.conversation_id !== conv.conversation_id;
            const convOtherViewers = getOtherViewers(viewersByResource, `chat:${conv.conversation_id}`, selfAgentId);
            return (
            <button key={conv.conversation_id}
              data-testid={`conv-item-${conv.conversation_id}`}
              onClick={() => {
                setSelectedConv(conv);
                // Marcar conversación bot como revisada localmente (el backend confirma en loadMessages)
                if (conv.status === 'bot' && !conv.agent_reviewed) {
                  setConversations(prev => prev.map(c =>
                    c.conversation_id === conv.conversation_id ? { ...c, agent_reviewed: true } : c));
                }
                // NO limpiar unread_count aquí — solo se limpia al responder o cerrar
              }}
              className={`w-full text-left px-4 py-3 border-b border-gray-800/60 transition-colors hover:bg-gray-900 ${
                hasUnread ? 'bg-indigo-950/30' : ''} ${
                selectedConv?.conversation_id === conv.conversation_id ? 'bg-gray-900 border-l-2 border-l-indigo-500' : ''}`}>
              <div className="flex items-start justify-between gap-2 mb-1">
                <span className={`text-sm truncate flex items-center gap-1.5 ${hasUnread ? 'text-white font-semibold' : 'text-white font-medium'}`}>
                  {hasUnread && <span className="w-2 h-2 rounded-full bg-indigo-400 flex-shrink-0" data-testid="unread-dot" />}
                  {getVisitorName(conv)}
                </span>
                <div className="flex items-center gap-1 flex-shrink-0">
                  {convOtherViewers.length > 0 && <ViewersStack viewers={convOtherViewers} />}
                  {hasUnread && (
                    <span data-testid={`unread-badge-${conv.conversation_id}`}
                      className="bg-indigo-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center">
                      {unread > 9 ? '9+' : unread}
                    </span>
                  )}
                  <ChannelBadge channel={conv.channel} />
                  <StatusBadge status={conv.status} />
                </div>
              </div>
              <p className={`text-xs truncate ${hasUnread ? 'text-gray-300' : 'text-gray-500'}`}>{conv.last_message?.content || conv.visitor_email || '—'}</p>
              <div className="flex items-center justify-between mt-0.5">
                <p className="text-gray-600 text-[10px]">
                  {new Date(conv.updated_at || conv.created_at).toLocaleString('es-ES', { day:'2-digit',month:'2-digit',hour:'2-digit',minute:'2-digit' })}
                </p>
                {conv.status === 'waiting' && (
                  <span className="text-amber-500 text-[10px] flex items-center gap-0.5">
                    <Clock size={9} /> {waitingLabel(conv.created_at)}
                  </span>
                )}
                {conv.status === 'bot' && !conv.agent_reviewed && (
                  <span data-testid={`bot-unreviewd-${conv.conversation_id}`}
                    className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-500/20 text-amber-400 border border-amber-500/30 flex items-center gap-0.5">
                    <Clock size={9} /> Sin revisar
                  </span>
                )}
                {/* Satisfacción */}
                {conv.satisfaction_rating && (
                  <span data-testid={`satisfaction-rating-${conv.conversation_id}`}
                    className="text-amber-400 text-[10px] flex items-center gap-0.5">
                    {'★'.repeat(conv.satisfaction_rating)}{'☆'.repeat(5 - conv.satisfaction_rating)}
                  </span>
                )}
              </div>
            </button>
            );
          })}
        </div>
      </div>

      {/* ── Hilo de mensajes ── */}
      {selectedConv ? (
        <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-800">
            <div className="min-w-0 flex-1 overflow-hidden">
              <span className="text-white font-medium text-sm truncate block">{getVisitorName(selectedConv)}</span>
              {selectedConv.visitor_email && <span className="text-gray-500 text-xs ml-2">{selectedConv.visitor_email}</span>}
              {selectedConv.channel === 'whatsapp' && (
                <span className="ml-2 inline-flex items-center gap-1 text-xs text-green-400 bg-green-500/10 border border-green-500/30 px-2 py-0.5 rounded-full">
                  <MessageCircle size={10} /> WhatsApp {selectedConv.wa_from ? `+${selectedConv.wa_from}` : ''}
                </span>
              )}
              {/* Badge idioma del visitante */}
              {selectedConv.language && selectedConv.language !== 'es' && (
                <span className="ml-2 inline-flex items-center gap-1 text-xs text-blue-400 bg-blue-500/10 border border-blue-500/20 px-2 py-0.5 rounded-full">
                  <Globe size={10} /> {selectedConv.language.toUpperCase()}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {/* Toggle traducción automática — solo si el visitante no habla español */}
              {selectedConv.language && selectedConv.language !== 'es' && (
                <button
                  data-testid="auto-translate-toggle"
                  onClick={() => setAutoTranslate(v => !v)}
                  title={autoTranslate ? 'Desactivar traducción automática' : 'Activar traducción automática (tus mensajes se traducirán al ' + selectedConv.language.toUpperCase() + ')'}
                  className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium border transition-colors ${
                    autoTranslate
                      ? 'bg-indigo-600 border-indigo-500 text-white'
                      : 'bg-gray-800 border-gray-700 text-gray-400 hover:text-white hover:border-gray-600'
                  }`}>
                  <Languages size={12} />
                  {autoTranslate ? `Traduciendo → ${selectedConv.language.toUpperCase()}` : 'Auto-traducir'}
                </button>
              )}
              <StatusBadge status={selectedConv.status} />
              {selectedConv.status !== 'agent' && (
                <Button size="sm" onClick={assignToMe} data-testid="assign-to-me-btn"
                  className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs gap-1">
                  <UserCheck size={12} /> Tomar
                </Button>
              )}
              <Button size="sm" variant="outline" onClick={() => setTransferModal(true)} data-testid="transfer-conv-btn"
                className="border-gray-700 text-gray-400 hover:text-white text-xs gap-1">
                <ArrowRightLeft size={12} /> Traspasar
              </Button>
              {/* Encuesta de satisfacción manual */}
              {selectedConv.status === 'closed' && !selectedConv.satisfaction_rating && (
                <Button size="sm" variant="outline" data-testid="trigger-satisfaction-btn"
                  onClick={async () => {
                    try {
                      await api.post(`/api/chat/conversations/${selectedConv.conversation_id}/satisfaction/trigger`);
                      toast.success('Encuesta enviada');
                    } catch { toast.error('Error al enviar encuesta'); }
                  }}
                  className="border-amber-700/50 text-amber-400 hover:text-amber-300 text-xs gap-1">
                  <Star size={11} /> Encuesta
                </Button>
              )}
              <Button size="sm" variant="outline" onClick={closeConv} data-testid="close-conv-btn"
                className="border-gray-700 text-gray-400 hover:text-red-400 text-xs gap-1">
                <X size={12} /> Cerrar
              </Button>
              {selectedConv.status !== 'closed' && (
                <Button size="sm" variant="outline" onClick={markAsUnread} data-testid="mark-unread-btn"
                  title="Marcar como no leída"
                  className="border-gray-700 text-gray-400 hover:text-amber-400 text-xs">
                  <EyeOff size={12} />
                </Button>
              )}
            </div>
          </div>

          {viewersOthersOnCurrent.length > 0 && <ViewersBanner viewers={viewersOthersOnCurrent} />}
          {selectedConv.customer_id && (
            <CustomerThreadsBanner
              customerId={selectedConv.customer_id}
              currentType="conversation"
              currentId={selectedConv.conversation_id}
            />
          )}
          {/* Ver conversación anterior (reapertura) */}
          {selectedConv.previous_conversation_id && (
            <div className="px-4 py-2 border-b border-gray-800 bg-purple-950/20">
              <button
                data-testid="previous-conv-link"
                onClick={() => {
                  const prev = conversations.find(c => c.conversation_id === selectedConv.previous_conversation_id);
                  if (prev) { setSelectedConv(prev); setFilter('closed'); }
                  else toast.info('La conversación anterior está en el historial de Cerradas');
                }}
                className="flex items-center gap-1.5 text-purple-400 hover:text-purple-300 text-xs transition-colors"
              >
                <History size={11} /> Ver conversación anterior
                {selectedConv.reopened_from && (
                  <span className="ml-1 text-[9px] text-purple-500 bg-purple-500/10 border border-purple-500/20 px-1.5 py-0.5 rounded-full">Reapertura rápida</span>
                )}
              </button>
            </div>
          )}
          {/* Valoración de satisfacción */}
          {selectedConv.satisfaction_rating && (
            <div className="px-4 py-2 border-b border-gray-800 bg-amber-950/10 flex items-center gap-3">
              <span data-testid="selected-conv-satisfaction"
                className="text-amber-400 text-sm">
                {'★'.repeat(selectedConv.satisfaction_rating)}{'☆'.repeat(5 - selectedConv.satisfaction_rating)}
              </span>
              <span className="text-gray-400 text-xs">{selectedConv.satisfaction_rating}/5</span>
              {selectedConv.satisfaction_comment && (
                <span className="text-gray-400 text-xs italic">"{selectedConv.satisfaction_comment}"</span>
              )}
            </div>
          )}

          <div ref={messagesContainerRef} className="flex-1 overflow-y-auto overflow-x-hidden p-4" onScroll={handleMessagesScroll}>
            {messages.map(msg => <MessageBubble key={msg.message_id} msg={msg} />)}
            {isTyping && (
              <div className="flex gap-2 mb-3">
                <div className="w-7 h-7 rounded-full bg-gray-700 flex items-center justify-center flex-shrink-0">
                  <User size={13} />
                </div>
                <div className="bg-gray-800 px-3 py-2 rounded-2xl rounded-tl-sm">
                  <span className="flex gap-1 items-center">
                    {[0,150,300].map(d => (
                      <span key={d} className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: `${d}ms` }} />
                    ))}
                  </span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <div className="border-t border-gray-800 p-3">
            {/* key=conversation_id resets input state automatically when switching chats */}
            <ReplyInput
              key={selectedConv.conversation_id}
              convStatus={selectedConv.status}
              onSend={stableSendReply}
              convId={selectedConv.conversation_id}
              orgId={selectedConv.organization_id}
            />
          </div>
        </div>
      ) : (
        <div className="flex-1 flex items-center justify-center text-gray-600">
          <div className="text-center">
            <MessageSquare size={40} className="mx-auto mb-3 opacity-20" />
            <p className="text-sm">Selecciona una conversación</p>
          </div>
        </div>
      )}

      {/* ── Panel Visitante ── */}
      <VisitorPanel conv={selectedConv} />

      {/* Modals */}
      {transferModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <div className="bg-gray-900 border border-gray-700 rounded-xl p-6 w-80">
            <h3 className="text-white font-semibold mb-4">Traspasar conversación</h3>
            {workers.filter(w => w.registered !== false).length === 0
              ? <p className="text-gray-400 text-sm">No hay otros agentes registrados.</p>
              : <div className="space-y-2">
                  {workers.filter(w => w.registered !== false).map(w => (
                    <button key={w.user_id} onClick={() => transferTo(w.user_id)}
                      data-testid={`transfer-to-${w.user_id}`}
                      className="w-full flex items-center justify-between px-3 py-2 rounded-lg bg-gray-800 hover:bg-gray-700 text-sm text-white transition-colors">
                      <span>{w.name || w.email || w.user_id}</span>
                      <span className={`text-xs ${w.is_online ? 'text-green-400' : 'text-gray-500'}`}>
                        {w.is_online ? 'Online' : 'Offline'}
                      </span>
                    </button>
                  ))}
                </div>
            }
            <Button onClick={() => setTransferModal(false)} variant="outline" className="w-full mt-4 border-gray-700 text-gray-400">
              Cancelar
            </Button>
          </div>
        </div>
      )}
      {qrManager && <QuickRepliesManager onClose={() => setQrManager(false)} />}
    </div>
  );
}

// ── Main ChatPage ──────────────────────────────────────────────

const VIEWS = [
  { id: 'conversations', label: 'Conversaciones', Icon: MessageSquare },
  { id: 'queue',         label: 'Cola',           Icon: Clock },
  { id: 'team',          label: 'Equipo',          Icon: BarChart2 },
];

export default function ChatPage() {
  const { organization } = useOrganization();
  const { user: clerkUser } = useUser();
  const orgId = organization?.id;

  const [view,       setView]      = useState('conversations');
  const [isOnline,   setIsOnline]  = useState(false);
  const [wsConnected,setWsConnected] = useState(false);
  const [workers,    setWorkers]   = useState([]);
  const [stats,      setStats]     = useState(null);
  const [soundEnabled, setSoundEnabled] = useState(
    () => localStorage.getItem('sera_chat_sound') !== 'off'
  );
  const [qrManager, setQrManager] = useState(false);

  const wsRef = useRef(null);

  const loadWorkers = useCallback(async () => {
    if (!orgId) return;
    try { const r = await api.get('/api/chat/workers'); setWorkers(r.data.workers || []); } catch {}
  }, [orgId]);

  const loadStats = useCallback(async () => {
    if (!orgId) return;
    try { const r = await api.get('/api/chat/stats'); setStats(r.data); } catch {}
  }, [orgId]);

  useEffect(() => { loadWorkers(); loadStats(); }, [loadWorkers, loadStats]);

  // Auto-refresh stats
  useEffect(() => {
    const t = setInterval(loadStats, 30000);
    return () => clearInterval(t);
  }, [loadStats]);

  const soundEnabledRef = useRef(soundEnabled);
  useEffect(() => { soundEnabledRef.current = soundEnabled; }, [soundEnabled]);

  // WebSocket — central handler (handles sound regardless of active view)
  useEffect(() => {
    if (!orgId) return;
    const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
    const wsBase = API_BASE.replace(/^https?/, 'wss').replace(/^http/, 'ws');
    let ws, retryTimeout;

    const connect = () => {
      ws = new WebSocket(`${wsBase}/api/chat/ws/workers/${orgId}`);
      wsRef.current = ws;
      ws.onopen  = () => {
        setWsConnected(true);
        // Reload stats on reconnect; ConversationsView handles its own conversation refresh
        loadStats();
      };
      ws.onerror = () => ws.close();
      const ka = setInterval(() => { if (ws.readyState === WebSocket.OPEN) ws.send('ping'); }, 25000);
      ws.onclose = () => {
        clearInterval(ka);
        setWsConnected(false);
        retryTimeout = setTimeout(connect, 4000);
      };
      ws.onmessage = (e) => {
        // Sound alert on escalated ONLY — new_conversation is handled by the bot initially
        try {
          const msg = JSON.parse(e.data);
          if (msg.type === 'escalated') {
            if (soundEnabledRef.current) playBeep();
            toast.info('Visitante solicita atención de un agente', { duration: 4000 });
          }
        } catch {}
        // Additional handling is overridden per-view by ConversationsView
      };
    };

    connect();
    return () => { clearTimeout(retryTimeout); try { ws?.close(); } catch {} };
  }, [orgId]);

  const toggleOnline = async () => {
    const next = !isOnline;
    setIsOnline(next);
    try {
      const name = clerkUser?.fullName || clerkUser?.firstName || '';
      const email = clerkUser?.primaryEmailAddress?.emailAddress || '';
      await api.put('/api/chat/workers/me/status', { is_online: next, name, email });
    }
    catch { setIsOnline(isOnline); }
  };

  const toggleSound = () => {
    const next = !soundEnabled;
    setSoundEnabled(next);
    localStorage.setItem('sera_chat_sound', next ? 'on' : 'off');
  };

  return (
    <div className="h-screen bg-gray-950 flex flex-col overflow-hidden">
      {/* Header */}
      <div className="border-b border-gray-800 px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-6">
          <div>
            <h1 className="text-white font-semibold text-lg flex items-center gap-2">
              <MessageSquare size={18} className="text-indigo-400" />
              Chat en vivo
            </h1>
          </div>
          {/* View tabs */}
          <div className="flex gap-1">
            {VIEWS.map(({ id, label, Icon }) => (
              <button key={id} onClick={() => setView(id)}
                data-testid={`view-tab-${id}`}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors
                  ${view === id ? 'bg-indigo-600 text-white' : 'text-gray-400 hover:text-white hover:bg-gray-800'}`}>
                <Icon size={12} /> {label}
              </button>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className={`flex items-center gap-1.5 text-xs ${wsConnected ? 'text-green-400' : 'text-gray-500'}`}>
            {wsConnected ? <Wifi size={12} /> : <WifiOff size={12} />}
            {wsConnected ? 'Conectado' : 'Desconectado'}
          </span>
          <Button data-testid="sound-toggle-btn" size="sm" variant="outline" onClick={toggleSound}
            className={`border-gray-700 text-xs gap-1 ${soundEnabled ? 'text-amber-400 border-amber-500/30' : 'text-gray-500'}`}>
            {soundEnabled ? <Bell size={12} /> : <BellOff size={12} />}
          </Button>
          <Button data-testid="quick-replies-manager-btn" size="sm" variant="outline"
            onClick={() => setQrManager(true)}
            className="border-gray-700 text-gray-400 hover:text-amber-400 text-xs gap-1">
            <Zap size={12} /> Respuestas rápidas
          </Button>
          <Button data-testid="worker-online-toggle" size="sm" onClick={toggleOnline}
            className={`text-xs gap-1.5 ${isOnline ? 'bg-green-600 hover:bg-green-700 text-white' : 'bg-gray-700 hover:bg-gray-600 text-gray-300'}`}>
            <div className={`w-2 h-2 rounded-full ${isOnline ? 'bg-green-200' : 'bg-gray-500'}`} />
            {isOnline ? 'Disponible' : 'Inactivo'}
          </Button>
          <Button size="sm" variant="outline" onClick={() => { loadStats(); loadWorkers(); }}
            className="border-gray-700 text-gray-400 hover:text-white text-xs">
            <RefreshCw size={12} />
          </Button>
        </div>
      </div>

      {/* Stats bar */}
      <StatsBar stats={stats} />

      {/* View content */}
      <div className="flex flex-1 overflow-hidden">
        {view === 'conversations' && (
          <ConversationsView workers={workers} loadStats={loadStats} wsRef={wsRef} wsConnected={wsConnected} soundEnabled={soundEnabled} stats={stats} />
        )}
        {view === 'queue' && (
          <QueueView workers={workers} />
        )}
        {view === 'team' && (
          <TeamView />
        )}
      </div>

      {qrManager && <QuickRepliesManager onClose={() => setQrManager(false)} />}
    </div>
  );
}
